import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { mfeResolver } from '@shared/mfe-resolver/mfe.resolver';

import { MFEPackageIds } from '../../interfaces/open-choice/mfe-details.enum';
import { AuthGuard } from './core/guards/auth/auth.guard';
import { divorceRoutes } from './divorce-routing.module';
import { checklistRoutes } from './document-checklist-routing.module';
import { giftingRoutes } from './gifting-routing.module';
import { inheritanceRoutes } from './inheritance-routing.module';
import { inheritedIraRoutes } from './inherited-ira-routing.module';
import { internalTransferRoutes } from './internal-transfer-routing.module';
import { minorRoutes } from './minor-routing.module';
import { multiInheritanceRoutes } from './multi-inheritance-routing.module';
import { DAEnrollComponent } from './secure/open-choice/da-enroll/da-enroll.component';
import { DAOfferComponent } from './secure/open-choice/da-offer/da-offer.component';
import { InvestmentStrategyComponent } from './secure/open-choice/investment-strategy/investment-strategy.component';
import { MultiAccountEnrollmentComponent } from './secure/open-choice/multi-account-enrollment/multi-account-enrollment.component';
import { PortfolioMatchComponent } from './secure/open-choice/portfolio-match/portfolio-match.component';
import { RiskQuizResultsComponent } from './secure/open-choice/risk-quiz-results/risk-quiz-results.component';
import { RiskQuizSetupComponent } from './secure/open-choice/risk-quiz-setup/risk-quiz-setup.component';
import { TaxFilingRetirementComponent } from './secure/open-choice/tax-filing-retirement/tax-filing-retirement.component';

const routes: Routes = [
  {
    path: '',
    redirectTo: 'guide',
    pathMatch: 'full',
  },
  // public pages - will live in each flow routing once entry points are updated
  {
    path: 'guide',
    loadChildren: () =>
      import('./public/inheritance-overview/inheritance-overview.module').then(
        (m) => m.InheritanceOverviewModule,
      ),
  },
  {
    path: 'gifting-welcome',
    loadChildren: () =>
      import('./public/gifting-overview/gifting-overview.module').then(
        (m) => m.GiftingOverviewModule,
      ),
  },
  {
    path: 'minor-welcome',
    loadChildren: () =>
      import('./public/minor-overview/minor-overview.module').then((m) => m.MinorOverviewModule),
  },
  // routing by flow
  { path: '', children: inheritanceRoutes },
  { path: '', children: divorceRoutes },
  { path: '', children: multiInheritanceRoutes },
  { path: '', children: giftingRoutes },
  { path: '', children: minorRoutes },
  { path: '', children: internalTransferRoutes },
  { path: '', children: inheritedIraRoutes },

  // Eager loading for open choice because VgimTaxesModule does not work properly with lazy loading
  {
    path: 'secure/open-choice',
    canActivate: [AuthGuard],
    children: [
      {
        path: 'offer',
        component: DAOfferComponent,
        title: 'Digital Advisor Offer | Vanguard',
        data: {
          pageId: 'gc-offer',
        },
      },
      {
        path: 'tax-filing-retirement',
        component: TaxFilingRetirementComponent,
        title: 'Tax Filing and Retirement | Vanguard',
        data: {
          pageId: 'gc-tax_filing',
        },
      },
      {
        path: 'risk-quiz-setup',
        component: RiskQuizSetupComponent,
        resolve: { data: mfeResolver(MFEPackageIds.RISK_QUIZ_PKG_ROE) },
        title: 'Risk Quiz Setup | Vanguard',
        data: {
          pageId: 'gc-risk_quiz-start',
        },
      },
      {
        path: 'portfolio-match',
        component: PortfolioMatchComponent,
        resolve: { data: mfeResolver(MFEPackageIds.RISK_QUIZ_PKG_ROE) },
        title: 'Portfolio Match | Vanguard',
        data: {
          pageId: 'gc-risk_quiz',
        },
      },
      {
        path: 'risk-quiz-results',
        component: RiskQuizResultsComponent,
        resolve: { data: mfeResolver(MFEPackageIds.RISK_QUIZ_PKG_ROE) },
        title: 'Risk Quiz Results | Vanguard',
        data: {
          pageId: 'gc-risk_quiz-results',
        },
      },
      {
        path: 'multi-account-enrollment',
        component: MultiAccountEnrollmentComponent,
        resolve: { data: mfeResolver(MFEPackageIds.PORTFOLIO_BUILDER_PKG) },
        title: 'Multi Account Enrollment | Vanguard',
        data: {
          pageId: 'gc-account_enrollment_selection',
        },
      },
      {
        path: 'investment-strategy',
        component: InvestmentStrategyComponent,
        title: 'Investment Strategy | Vanguard',
        data: {
          pageId: 'gc-investment_strategy',
        },
      },
      {
        path: 'enroll',
        component: DAEnrollComponent,
        title: 'Enroll in Digital Advisor | Vanguard',
        resolve: { data: mfeResolver(MFEPackageIds.PORTFOLIO_BUILDER_PKG) },
        data: {
          pageId: 'digital_advisor_enroll',
        },
      },
    ],
  },
  // shared routing
  {
    path: 'secure/testmonkey',
    loadChildren: () => import('./test-monkey/test-monkey.module').then((m) => m.TestMonkeyModule),
  },
  {
    path: 'secure',
    canActivate: [AuthGuard],
    loadChildren: () => import('./secure/review/review.module').then((m) => m.ReviewModule),
  },
  {
    path: 'secure',
    canActivate: [AuthGuard],
    loadChildren: () => import('./secure/confirm/confirm.module').then((m) => m.ConfirmModule),
  },
  // these will be updated in ineligible refactor
  {
    path: '',
    loadChildren: () =>
      import('./public/ineligible/ineligible.module').then((m) => m.IneligibleModule),
  },
  {
    path: 'error',
    loadChildren: () => import('./public/error/error.module').then((m) => m.ErrorModule),
  },
  { path: '', children: checklistRoutes },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {})],
  exports: [RouterModule],
})
export class AppRoutingModule {}
